/**
 * Amazon Ads — Shared Report Orchestration (Story 19.15, Task 1)
 *
 * Extracts the submit → poll → download → parse pipeline so SP / SB / SD all
 * share a single source of truth. Without this extraction, sb-fetch-metrics +
 * sd-fetch-metrics would each duplicate ~600 LOC from sp-fetch-metrics — that's
 * 1200 LOC of copy-paste, 1200 LOC of test duplication, and a high probability
 * of one-off bugs (e.g., SB doesn't get the inline-token-refresh wrapper that
 * SP got; SD doesn't get the cache-validation step that SP got).
 *
 * This module is the cross-product pipeline; DSP in 19.16 plugs in via the
 * same orchestration. Product-specific column mapping + derived-metric logic
 * stays in the per-product fetcher (sp-/sb-/sd-fetch-metrics.ts).
 *
 * Architecture:
 *   1. Validate (adProduct, reportTypeId) discriminated-union pair (AC #11
 *      runtime defense).
 *   2. submit POST → reportId.
 *   3. Insert into amazon_ads_report_jobs D1 BEFORE polling (carry-forward
 *      19.14 AC #1 invariant — if the Worker dies mid-flight the phantom-report
 *      reaper Cron cancels orphans).
 *   4. Poll with cadence 1s → 2s → 4s → 8s → 8s → 8s (max 30s wall-clock —
 *      Story 19.14 AC #2).
 *   5. Download via SSRF allowlist + 50MB cap + line-by-line NDJSON parse via
 *      caller-provided `parseRecord` (the only product-specific hook).
 *   6. Emit the 9 lifecycle structured logs from Story 19.15 AC #16 with
 *      `correlationId` chained through.
 */

import {
  AMAZON_ADS_SP_PENDING_STATUSES,
  AMAZON_ADS_SP_POLL_CADENCE_MS,
  AMAZON_ADS_SP_REPORT_TIMEOUT_MS,
  AMAZON_ADS_SP_TERMINAL_SUCCESS_STATUSES,
  downloadAndParseReport,
  parseSpReportError,
  pollReport,
  SpReportApiError,
  submitAmazonAdsReport,
  validateAdProductReportTypePair,
  type AmazonAdsAdProduct,
  type AmazonAdsReportTypeId,
  type AnyAmazonAdsReportRequestBody,
  type SpReportRecord,
} from "../reports";

/**
 * Story 19.16 AC #10/#11 — DSP-aware download contract. The default
 * `downloadAndParseReport` from 19.14 expects gzipped NDJSON; for DSP the
 * caller passes `downloadAndParseDspReport` (flat JSON array).
 */
export interface DownloadResult {
  readonly records: SpReportRecord[];
  readonly warnings: string[];
  readonly bytesProcessed: number;
}
export type DownloadImpl = (
  url: string,
  fetchImpl: typeof fetch
) => Promise<DownloadResult>;
import type { AmazonAdsRegion } from "../oauth";

// Re-exported for clarity; the ms-budget constant is shared across SP/SB/SD.
export const AMAZON_ADS_REPORT_TIMEOUT_MS = AMAZON_ADS_SP_REPORT_TIMEOUT_MS;

/**
 * D1 helper bag — passed in so the orchestration stays agnostic to whether the
 * caller is the adapter, a queue worker, or the reaper. Mirrors the SP-only
 * shape from Story 19.14's `SpReportJobsApi` so existing SP code paths can
 * adapt with minimal changes.
 */
export interface AmazonAdsReportJobsApi {
  insert(args: {
    id: string;
    report_id: string;
    tenant_id: string;
    integration_id: string;
    request_hash: string;
    correlation_id: string;
    /** Story 19.15 Task 16 — optional ad-product enrichment (forensics-only). */
    ad_product?: AmazonAdsAdProduct;
    now?: number;
  }): Promise<void>;
  findInFlightByHash(args: {
    integration_id: string;
    request_hash: string;
    now?: number;
  }): Promise<{
    id: string;
    report_id: string;
    status: string;
  } | null>;
  countInFlight(args: { integration_id: string; now?: number }): Promise<number>;
  markStatus(args: {
    id: string;
    status:
      | "PENDING"
      | "PROCESSING"
      | "COMPLETED"
      | "FAILED"
      | "CANCELLED"
      | "reaper_failed"
      | "cancelled_validation_probe";
    completed_at?: number | null;
  }): Promise<{ updated: boolean }>;
}

export interface AmazonAdsKvApi {
  get(key: string): Promise<string | null>;
  put(key: string, value: string, ttlSec: number): Promise<void>;
}

/**
 * Story 19.15 AC #7 — multi-product safeguard. Default lowered from 19.14's 12
 * to 9 to absorb the worst-case "5 widgets × 3 ad-products = 15 reports per
 * dashboard load" pressure (Round 2 Pre-mortem Failure C — single highest-
 * blast-radius scope-extension change for this story).
 *
 * 6-row buffer: 3 reaper rows + 2 D1 transaction-race + 1 cap-check-vs-POST race.
 */
export const AMAZON_ADS_MAX_REPORTS_PER_INTEGRATION = 9;

export interface RunAmazonAdsReportPipelineInput<TRecord> {
  readonly accessToken: string;
  readonly clientId: string;
  readonly profileId: string;
  readonly region: AmazonAdsRegion;
  readonly tenantId: string;
  readonly integrationId: string;
  readonly body: AnyAmazonAdsReportRequestBody;
  readonly requestHash: string;
  readonly correlationId: string;
  readonly adProduct: AmazonAdsAdProduct;
  readonly reportTypeId: AmazonAdsReportTypeId;
  readonly jobs: AmazonAdsReportJobsApi;
  readonly parseRecord: (record: SpReportRecord) => TRecord;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly newJobId?: () => string;
  /**
   * Optional override for the in-flight cap. Defaults to
   * `AMAZON_ADS_MAX_REPORTS_PER_INTEGRATION` (9 for 19.15+).
   */
  readonly maxInFlightReports?: number;
  /**
   * Story 19.16 AC #11 — terminal-success statuses override. DSP uses
   * `["SUCCESS"]`; SP/SB/SD default to `["COMPLETED", "SUCCESS"]`. Round 4
   * QA Gap 3 — when `adProduct === "DSP"` and this override is OMITTED, the
   * pipeline THROWS at orchestration entry to kill the silent slow-rot
   * failure mode.
   */
  readonly terminalSuccessStatuses?: ReadonlyArray<string>;
  /**
   * Story 19.16 AC #11 — pending statuses override. DSP uses `["IN_PROGRESS"]`;
   * SP/SB/SD default to `["PENDING", "IN_QUEUE", "PROCESSING"]`. Same Round 4
   * QA Gap 3 contract as `terminalSuccessStatuses`.
   */
  readonly pendingStatuses?: ReadonlyArray<string>;
  /**
   * Story 19.16 AC #10 — download implementation override. DSP passes
   * `downloadAndParseDspReport` (flat JSON, no gzip); SP/SB/SD default to
   * `downloadAndParseReport` (gzipped NDJSON).
   */
  readonly downloadImpl?: DownloadImpl;
  /**
   * Story 19.16 AC #10 — SSRF allowlist override. DSP passes a function that
   * dispatches to the tighter `corvo-reports.s3*.amazonaws.com` regex;
   * SP/SB/SD default behavior is encapsulated inside `downloadAndParseReport`.
   * When supplied, this guard is checked BEFORE invoking `downloadImpl` for
   * defense-in-depth (the inner `downloadImpl` may also check independently).
   */
  readonly isSafeUrlImpl?: (url: string) => boolean;
}

export type RunAmazonAdsReportPipelineResult<TRecord> =
  | {
      readonly kind: "ok";
      readonly records: TRecord[];
      readonly warnings: string[];
      readonly reportId: string;
    }
  | {
      readonly kind: "error";
      readonly code:
        | "rate_limited"
        | "invalid_response"
        | "provider_unavailable"
        | "permission_denied"
        | "api_not_enabled"
        | "token_expired"
        | "token_invalid"
        | "brand_registry_required"
        | "dsp_not_enabled"
        | "network_error";
      readonly message: string;
      readonly retryable: boolean;
      readonly reportId?: string;
    }
  | {
      readonly kind: "pending";
      readonly message: string;
      readonly reportId: string;
    };

/**
 * Story 19.15 Task 1.1 — multi-product report pipeline. The function:
 *
 *   - validates the discriminated-union (adProduct, reportTypeId) pair (AC #11)
 *   - dedups via in-flight `request_hash` lookup (carry-forward 19.14)
 *   - enforces the 9-row safeguard (AC #7)
 *   - submits + persists to D1 BEFORE polling (carry-forward 19.14 AC #1)
 *   - polls with cadence 1/2/4/8/8/8 ms (max 30s wall-clock)
 *   - downloads via SSRF allowlist + 50MB cap (carry-forward 19.14 AC #3)
 *   - line-parses NDJSON with malformed-line tolerance (carry-forward 19.14 AC #4)
 *   - emits 9+ lifecycle structured-logs (AC #16) with correlationId chained
 */
export async function runAmazonAdsReportPipeline<TRecord>(
  input: RunAmazonAdsReportPipelineInput<TRecord>
): Promise<RunAmazonAdsReportPipelineResult<TRecord>> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const now = input.now ?? Date.now;
  const sleep =
    input.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const newJobId = input.newJobId ?? (() => crypto.randomUUID());
  const maxInFlight =
    input.maxInFlightReports ?? AMAZON_ADS_MAX_REPORTS_PER_INTEGRATION;

  // Story 19.16 Round 4 QA Gap 3 — DSP requires explicit status overrides.
  // Forgetting them silently uses SP's `["PENDING", "IN_QUEUE", "PROCESSING"]`
  // which would NEVER match DSP's `IN_PROGRESS`, causing 30-second-budget
  // timeouts on every DSP fetch. Throw loud at orchestration entry to kill
  // the slow-rot failure mode.
  if (
    input.adProduct === "DSP" &&
    (!input.terminalSuccessStatuses || !input.pendingStatuses)
  ) {
    throw new Error(
      "Amazon DSP pipeline requires explicit terminalSuccessStatuses + pendingStatuses overrides — see Story 19.16 AC #11 / Round 4 QA Gap 3"
    );
  }

  const terminalSuccessStatuses =
    input.terminalSuccessStatuses ?? AMAZON_ADS_SP_TERMINAL_SUCCESS_STATUSES;
  const pendingStatuses =
    input.pendingStatuses ?? AMAZON_ADS_SP_PENDING_STATUSES;
  const downloadImpl: DownloadImpl =
    input.downloadImpl ?? downloadAndParseReport;

  // AC #11 runtime defense — fail loud BEFORE the network call. Compile-time
  // the discriminated union prevents this; runtime catches `as any` cheating.
  validateAdProductReportTypePair(
    input.adProduct,
    input.reportTypeId,
    "runAmazonAdsReportPipeline"
  );

  // Lifecycle log — fetchmetrics_started equivalent at the orchestration level.
  logEvent("amazon_ads_fetchmetrics_started", {
    correlationId: input.correlationId,
    adProduct: input.adProduct,
    reportTypeId: input.reportTypeId,
    integrationId: input.integrationId,
  });

  const startTime = now();
  const remainingBudgetMs = (): number =>
    Math.max(0, AMAZON_ADS_SP_REPORT_TIMEOUT_MS - (now() - startTime));

  // Step 1 — in-flight dedup (request_hash already includes adProduct +
  // reportTypeId via canonicalize over the configuration object).
  const existing = await input.jobs.findInFlightByHash({
    integration_id: input.integrationId,
    request_hash: input.requestHash,
    now: now(),
  });

  let jobId: string;
  let reportId: string;
  if (existing) {
    jobId = existing.id;
    reportId = existing.report_id;
    logEvent("amazon_ads_report_resumed", {
      correlationId: input.correlationId,
      reportId,
      adProduct: input.adProduct,
    });
  } else {
    // Step 2 — concurrent-cap safeguard (AC #7 — 9 in 19.15+).
    const inFlight = await input.jobs.countInFlight({
      integration_id: input.integrationId,
      now: now(),
    });
    if (inFlight >= maxInFlight) {
      logEvent("amazon_ads_safeguard_breach", {
        correlationId: input.correlationId,
        adProduct: input.adProduct,
        inFlight,
        cap: maxInFlight,
      });
      return {
        kind: "error",
        code: "rate_limited",
        message: `Amazon Ads in-flight report cap reached (${inFlight}/${maxInFlight} reserved). Try again in 30 seconds.`,
        retryable: true,
      };
    }

    // Step 3 — submit.
    let submission;
    try {
      submission = await submitAmazonAdsReport(
        {
          accessToken: input.accessToken,
          clientId: input.clientId,
          profileId: input.profileId,
          region: input.region,
          body: input.body,
        },
        fetchImpl
      );
    } catch (err) {
      if (err instanceof SpReportApiError) {
        const parsed = parseSpReportError(err, input.adProduct);
        if (parsed.logEvent) {
          logEvent(parsed.logEvent, {
            correlationId: input.correlationId,
            adProduct: input.adProduct,
            providerStatus: err.status,
          });
        }
        return {
          kind: "error",
          code: parsed.code,
          message: err.message,
          retryable: parsed.retryable,
        };
      }
      return {
        kind: "error",
        code: "network_error",
        message: err instanceof Error ? err.message : String(err),
        retryable: true,
      };
    }
    reportId = submission.reportId;
    jobId = newJobId();
    await input.jobs.insert({
      id: jobId,
      report_id: reportId,
      tenant_id: input.tenantId,
      integration_id: input.integrationId,
      request_hash: input.requestHash,
      correlation_id: input.correlationId,
      ad_product: input.adProduct,
      now: now(),
    });
    logEvent("amazon_ads_report_submitted", {
      correlationId: input.correlationId,
      reportId,
      adProduct: input.adProduct,
      reportTypeId: input.reportTypeId,
    });
  }

  // Step 4 — poll loop with cadence + remaining wall-clock budget.
  let downloadUrl: string | undefined;
  let pollIteration = 0;
  let consecutivePollErrors = 0;
  const MAX_CONSECUTIVE_POLL_ERRORS = 3;

  for (const cadenceMs of AMAZON_ADS_SP_POLL_CADENCE_MS) {
    const budget = remainingBudgetMs();
    if (budget <= 0) break;
    await sleep(Math.min(cadenceMs, budget));
    pollIteration++;
    let pollResult;
    try {
      pollResult = await pollReport(
        {
          accessToken: input.accessToken,
          clientId: input.clientId,
          profileId: input.profileId,
          region: input.region,
          reportId,
          // Story 19.16 Round 1 review fix — DSP polls via
          // `/accounts/{dspAccountId}/dsp/reports/{reportId}`.
          adProduct: input.adProduct,
        },
        fetchImpl
      );
      consecutivePollErrors = 0;
    } catch (err) {
      consecutivePollErrors++;
      if (err instanceof SpReportApiError) {
        const parsed = parseSpReportError(err, input.adProduct);
        if (parsed.logEvent) {
          logEvent(parsed.logEvent, {
            correlationId: input.correlationId,
            adProduct: input.adProduct,
            providerStatus: err.status,
          });
        }
        if (!parsed.retryable) {
          await input.jobs.markStatus({
            id: jobId,
            status: "FAILED",
            completed_at: now(),
          });
          return {
            kind: "error",
            code: parsed.code,
            message: err.message,
            retryable: false,
            reportId,
          };
        }
        if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
          return {
            kind: "error",
            code: parsed.code,
            message: `${err.message} (after ${consecutivePollErrors} consecutive poll errors)`,
            retryable: true,
            reportId,
          };
        }
      } else if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        return {
          kind: "error",
          code: "network_error",
          message:
            err instanceof Error
              ? `${err.message} (after ${consecutivePollErrors} consecutive poll errors)`
              : String(err),
          retryable: true,
          reportId,
        };
      }
      logEvent("amazon_ads_report_polling_iteration", {
        correlationId: input.correlationId,
        reportId,
        adProduct: input.adProduct,
        pollNumber: pollIteration,
        elapsedMs: now() - startTime,
        status: "error",
      });
      continue;
    }

    logEvent("amazon_ads_report_polling_iteration", {
      correlationId: input.correlationId,
      reportId,
      adProduct: input.adProduct,
      pollNumber: pollIteration,
      elapsedMs: now() - startTime,
      status: pollResult.status,
    });

    if (terminalSuccessStatuses.includes(pollResult.status)) {
      downloadUrl = pollResult.url;
      await input.jobs.markStatus({ id: jobId, status: "PROCESSING" });
      break;
    }
    if (pollResult.status === "FAILED") {
      await input.jobs.markStatus({
        id: jobId,
        status: "FAILED",
        completed_at: now(),
      });
      logEvent("amazon_ads_report_failed", {
        correlationId: input.correlationId,
        reportId,
        adProduct: input.adProduct,
        errorCode: "invalid_response",
        errorReason: pollResult.failureReason ?? "Amazon Ads report FAILED",
      });
      return {
        kind: "error",
        code: "invalid_response",
        message: pollResult.failureReason ?? "Amazon Ads report FAILED",
        retryable: false,
        reportId,
      };
    }
    if (pollResult.status === "CANCELLED") {
      await input.jobs.markStatus({
        id: jobId,
        status: "CANCELLED",
        completed_at: now(),
      });
      return {
        kind: "error",
        code: "invalid_response",
        message: "Amazon Ads report CANCELLED externally",
        retryable: false,
        reportId,
      };
    }
    if (!pendingStatuses.includes(pollResult.status)) {
      logEvent("amazon_ads_status_alias_observed", {
        correlationId: input.correlationId,
        reportId,
        adProduct: input.adProduct,
        status: pollResult.status,
      });
    }
  }

  if (!downloadUrl) {
    // Wall-clock exceeded — keep D1 row alive (do NOT mark terminal). Caller
    // sees a non-fatal pending state + retries on next invocation.
    return {
      kind: "pending",
      message: `report_pending_retry_later (poll iterations=${pollIteration})`,
      reportId,
    };
  }

  // Step 5 — download + decompress + parse. DSP overrides this with
  // `downloadAndParseDspReport` (flat JSON, no gzip); SP/SB/SD use the
  // gzipped-NDJSON default. The `isSafeUrlImpl` override (when provided)
  // gives a defense-in-depth pre-check for DSP's tighter SSRF allowlist.
  if (input.isSafeUrlImpl && !input.isSafeUrlImpl(downloadUrl)) {
    logEvent("amazon_ads_suspicious_download_url", {
      correlationId: input.correlationId,
      reportId,
      adProduct: input.adProduct,
    });
    await input.jobs.markStatus({
      id: jobId,
      status: "FAILED",
      completed_at: now(),
    });
    return {
      kind: "error",
      code: "invalid_response",
      message: "suspicious_download_url",
      retryable: false,
      reportId,
    };
  }

  let parsed;
  try {
    parsed = await downloadImpl(downloadUrl, fetchImpl);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("suspicious_download_url")) {
      logEvent("amazon_ads_suspicious_download_url", {
        correlationId: input.correlationId,
        reportId,
        adProduct: input.adProduct,
      });
    }
    if (msg.includes("report_oversized")) {
      logEvent("amazon_ads_oversized_report", {
        correlationId: input.correlationId,
        reportId,
        adProduct: input.adProduct,
      });
    }
    await input.jobs.markStatus({
      id: jobId,
      status: "FAILED",
      completed_at: now(),
    });
    return {
      kind: "error",
      code: "invalid_response",
      message: msg,
      retryable: false,
      reportId,
    };
  }

  const parsedRecords: TRecord[] = [];
  for (const r of parsed.records) {
    parsedRecords.push(input.parseRecord(r));
  }

  // Step 6 — mark COMPLETED.
  await input.jobs.markStatus({
    id: jobId,
    status: "COMPLETED",
    completed_at: now(),
  });
  logEvent("amazon_ads_report_completed", {
    correlationId: input.correlationId,
    reportId,
    adProduct: input.adProduct,
    reportTypeId: input.reportTypeId,
    recordCount: parsedRecords.length,
  });

  return {
    kind: "ok",
    records: parsedRecords,
    warnings: parsed.warnings,
    reportId,
  };
}

function logEvent(event: string, details: Record<string, unknown>): void {
  // Privacy: NEVER log raw access token / refresh token / S3 path component /
  // accountInfo.name / customer email / advertiser-side identifiers. Story
  // 19.15 AC #16 — caller is responsible for not passing sensitive values into
  // `details`.
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
